import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, Inbox } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 30000;

const TYPE_BADGE = {
  material_request_submitted: { label: 'Approval', cls: 'bg-orange-100 text-orange-800' },
  material_request_approved: { label: 'Approved', cls: 'bg-green-100 text-green-800' },
  material_request_rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-800' },
  order_placed: { label: 'Order', cls: 'bg-blue-100 text-blue-800' },
  order_status_changed: { label: 'Order', cls: 'bg-blue-100 text-blue-800' },
  invoice_created: { label: 'Invoice', cls: 'bg-purple-100 text-purple-800' },
  delivery_note_created: { label: 'Delivery', cls: 'bg-cyan-100 text-cyan-800' },
  carton_scanned: { label: 'Scan', cls: 'bg-emerald-100 text-emerald-800' },
  qr_batch_generated: { label: 'QR Batch', cls: 'bg-indigo-100 text-indigo-800' },
  low_stock: { label: 'Low Stock', cls: 'bg-rose-100 text-rose-800' },
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString();
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { headers: authHeaders() });
      setUnread(res.data?.count || 0);
    } catch (_) {
      // silent
    }
  };

  const fetchList = async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=10`, { headers: authHeaders() });
      setItems(res.data || []);
    } catch (_) {
      // silent
    }
  };

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, { headers: authHeaders() });
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (_) {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { headers: authHeaders() });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (_) {}
  };

  const onItemClick = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
        aria-label="Notifications"
        data-testid="notification-bell"
      >
        <Bell size={20} className="text-slate-700" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EA5829] text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-w-[90vw] bg-white border border-slate-200 rounded-lg shadow-xl z-[60] overflow-hidden"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
            <div className="font-semibold text-slate-900">Notifications</div>
            {items.some((n) => !n.read) && (
              <button
                onClick={markAllRead}
                className="text-xs text-[#8FC043] hover:underline flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">
              <Inbox size={28} className="mx-auto mb-2 text-slate-300" />
              No notifications yet
            </div>
          ) : (
            <ul className="max-h-96 overflow-y-auto divide-y divide-slate-100">
              {items.map((n) => {
                const badge = TYPE_BADGE[n.type] || { label: n.type, cls: 'bg-slate-100 text-slate-700' };
                return (
                  <li
                    key={n.id}
                    onClick={() => onItemClick(n)}
                    className={`px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                      n.read ? 'bg-white' : 'bg-blue-50/40'
                    }`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="flex items-start gap-2 mb-1">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>
                        {badge.label}
                      </span>
                      {!n.read && <span className="w-2 h-2 bg-[#EA5829] rounded-full mt-1.5" />}
                      <span className="ml-auto text-[10px] text-slate-500 whitespace-nowrap">
                        {formatTime(n.created_at)}
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-slate-900 leading-tight">
                      {n.title}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</div>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="border-t bg-slate-50 px-4 py-2 text-center">
            <button
              onClick={() => {
                setOpen(false);
                navigate('/notifications');
              }}
              className="text-sm text-[#8FC043] hover:underline font-semibold"
              data-testid="view-all-notifications-btn"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
